/**
 * Phase 41 Plan 41-09 Task 2 — Slack User ID PATCH client + verify stub.
 *
 * Endpoint: PATCH /api/auth/preferences (verified 2026-05-11 against
 * backend/src/api/routes/auth.ts line 145; per checker I-21 this is the
 * canonical endpoint — RESEARCH.md line 111 mention of PATCH /api/users/me
 * is outdated). Backend body validation accepts:
 *   { slackUserId?: string | null, ...other preferences }
 *   slackUserId ~ /^[UW][A-Z0-9]{8,18}$/  (Phase 46 follow-up quick 260521-vct)
 *
 * Defense-in-depth: backend validates at three layers (Fastify body schema +
 * Zod re-validation in updatePreferences controller + the regex above), so the
 * frontend sends the raw User ID and surfaces the 400 via toast on rejection.
 *
 * verifySlackUserIdStub — per Open Q4 (resolved in 41-RESEARCH.md), v7.0 ships
 * a 900ms setTimeout stub. Production Slack workspace lookup is deferred to v7.1.
 * Stub rule: any User ID except one containing "admin" verifies successfully.
 */

import { apiPatch } from './client';

export interface UpdateUserPreferencesInput {
  slackUserId?: string | null;
  // Phase 60 (Plan 60-03 / SMS-03) — Phone number is a sibling User column;
  // PATCH /api/auth/preferences accepts it alongside slackUserId and runs the
  // E.164 regex `^\+[1-9]\d{1,14}$` server-side (column renamed in Phase 60
  // Plan 60-01).
  phoneNumber?: string | null;
  preferences?: Record<string, unknown>;
}

export interface UpdateUserPreferencesResponse {
  data: {
    slackUserId?: string | null;
    phoneNumber?: string | null;
    preferences?: Record<string, unknown> | null;
  };
}

export function updateUserPreferences(
  input: UpdateUserPreferencesInput,
): Promise<UpdateUserPreferencesResponse> {
  return apiPatch<UpdateUserPreferencesResponse>(`/auth/preferences`, input);
}

/**
 * Stub per Open Q4 — production Slack workspace lookup deferred to v7.1.
 *
 * Resolves after 900ms (matches the prototype's perceived latency). Returns
 * `true` for any User ID except one containing "admin" (case-insensitive); the
 * "admin" rejection is the deterministic failure path the test suite exercises
 * to prove the toast + disabled-toggle branch works.
 */
export async function verifySlackUserIdStub(userId: string): Promise<boolean> {
  await new Promise((resolve) => setTimeout(resolve, 900));
  return !userId.toLowerCase().includes('admin');
}

/**
 * @deprecated Use verifySlackUserIdStub instead. Kept for test-mock backward compat
 * until the PreferencesPage test is updated.
 */
export const verifySlackHandleStub = verifySlackUserIdStub;
